/* eslint-disable react-hooks/exhaustive-deps */
// The call pill's realtime client. Feeds one call map from three sources:
//   1. VOICE_CALL_WINDOW_EVENT — every `voice.call` payload LiveNotificationHandler
//      receives on this tab's socket;
//   2. the other CRM tabs of the same user (BroadcastChannel), so hide,
//      dismiss and events missed by one tab's socket stay in step;
//   3. the live-calls endpoint: on load, on practice switch, when the tab
//      comes back, and every LIVE_RECHECK_MS while a call is in progress, so a
//      refresh rebuilds the pill and a lost end event can't leave it open.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callApi } from '../../utils/apiCall';
import { VOICE_CALL_WINDOW_EVENT } from '../../sections/notifications/voiceCallScreenPop';
import {
    CALL_STATE, applyCallEvent, expireCalls, hasLiveCall, mergeCalls, nextExpiryIn, notificationFor,
    reconcileLiveCalls, selectView, tombstone,
} from './callPillModel';
import { DEFAULT_POSITION } from './callPillPosition';
import {
    DND_EVENT, canPlayRing, claimRing, dndKey, forgetRing, isFrontTab, openChannel, playRingTone, readDnd, showDesktopNotification, soundOn, unlockRingTone,
} from './callPillSync';

// Contract B2: GET live calls in progress for the current user. `caps` tells the backend this
// browser can show call alerts (bell rows, call push); an older frontend never sends it, so it
// never gets them (CU-869fmcc9r).
export const CALL_ALERTS_CAPABILITY = 'call-alerts-v1';
export const liveCallsUrl = (practiceId) => `/api/v1/practices/${practiceId}/calls/live/?caps=${CALL_ALERTS_CAPABILITY}`;
const REFETCH_AFTER_HIDDEN_MS = 20 * 1000;
export const LIVE_RECHECK_MS = 45 * 1000;
// How long a background tab waits for a front tab to answer before raising a
// desktop notification.
const FRONT_TAB_WAIT_MS = 250;

// Minimised to the round bubble (CU-869fmcc9r): per user, remembered across
// calls and tabs until they expand it again.
export const minimisedKey = (userId) => `callPill:minimised:${userId ?? 'anon'}`;
const readMinimised = (userId) => {
    try { return window.localStorage.getItem(minimisedKey(userId)) === '1'; } catch { return false; }
};
const writeMinimised = (userId, on) => {
    try { window.localStorage.setItem(minimisedKey(userId), on ? '1' : '0'); } catch { /* storage unavailable */ }
};

const rowsOf = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return null;
};

export default function useCallPill({ userId, practiceId, enabled = true, onNotificationClick, deskName = '' } = {}) {
    const active = Boolean(enabled && userId && practiceId);
    const scope = active ? `${userId}:${practiceId}` : null;
    const [calls, setCalls] = useState({});
    const [hidden, setHidden] = useState([]);
    const [dnd, setDndState] = useState(() => readDnd(userId));
    // Every call's pill starts top centre, under the header (DEFAULT_POSITION). Dragging
    // still moves it, for that call only: a nudge made days ago must not decide where the
    // next call appears, which is what remembering it across calls did.
    const [position, setPositionState] = useState(DEFAULT_POSITION);
    const [minimised, setMinimisedState] = useState(() => readMinimised(userId));
    // Which of several live calls the pill shows: set by its "+N" button, null = the
    // usual priority order (my own call, then the newest ringing).
    const [focusedId, setFocusedId] = useState(null);
    const deskNameRef = useRef(deskName);
    deskNameRef.current = deskName;
    const channelRef = useRef(null);
    const notificationsRef = useRef(new Map());
    const liveEndpointMissingRef = useRef(false);
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const callsRef = useRef(calls);
    callsRef.current = calls;
    const hiddenRef = useRef(hidden);
    hiddenRef.current = hidden;
    const dndRef = useRef(dnd);
    dndRef.current = dnd;
    const onClickRef = useRef(onNotificationClick);
    onClickRef.current = onNotificationClick;
    const frontRepliesRef = useRef(new Set());
    const pendingTimersRef = useRef(new Set());

    const ingest = useCallback((payload) => {
        setCalls((prev) => applyCallEvent(prev, payload));
    }, []);

    // Reset on user / practice switch: another practice's calls must never show.
    useEffect(() => {
        setCalls({});
        setHidden([]);
        setFocusedId(null);
        liveEndpointMissingRef.current = false;
        setDndState(readDnd(userId));
        setPositionState(DEFAULT_POSITION);
        setMinimisedState(readMinimised(userId));
    }, [userId, practiceId]);

    // Browsers only play sound after the user has interacted with the page:
    // the first click or key press unlocks the ring for later background calls.
    useEffect(() => {
        if (!active) return undefined;
        const unlock = () => unlockRingTone();
        window.addEventListener('pointerdown', unlock, { capture: true });
        window.addEventListener('keydown', unlock, { capture: true });
        return () => {
            window.removeEventListener('pointerdown', unlock, { capture: true });
            window.removeEventListener('keydown', unlock, { capture: true });
        };
    }, [active]);

    // 1. This tab's socket.
    useEffect(() => {
        if (!active) return undefined;
        const onEvent = (e) => {
            const payload = e?.detail;
            if (!payload) return;
            ingest(payload);
            channelRef.current?.post({ type: 'event', practiceId, payload });
        };
        window.addEventListener(VOICE_CALL_WINDOW_EVENT, onEvent);
        return () => window.removeEventListener(VOICE_CALL_WINDOW_EVENT, onEvent);
    }, [active, ingest, practiceId]);

    // 2. Other tabs.
    useEffect(() => {
        if (!active) return undefined;
        const channel = openChannel(userId);
        channelRef.current = channel;
        const unsubscribe = channel.subscribe((msg) => {
            // A tab on another practice shares the channel (same user); ignore it.
            if (String(msg.practiceId) !== String(practiceId)) return;
            switch (msg.type) {
            case 'event':
                ingest(msg.payload);
                break;
            case 'hide':
                setHidden((h) => (h.includes(String(msg.callId)) ? h : [...h, String(msg.callId)]));
                break;
            case 'unhide_all':
                setHidden([]);
                break;
            case 'dismiss':
                setCalls((prev) => tombstone(prev, msg.callId, { final: msg.final !== false, occurredAt: msg.final === false ? prev?.[msg.callId]?.occurredAt ?? null : null }));
                break;
            case 'sync_request':
                if (Object.keys(callsRef.current).length || hiddenRef.current.length) {
                    channel.post({ type: 'sync_state', practiceId, calls: callsRef.current, hidden: hiddenRef.current });
                }
                break;
            case 'sync_state':
                setCalls((prev) => mergeCalls(prev, msg.calls));
                if (Array.isArray(msg.hidden) && msg.hidden.length) {
                    setHidden((h) => Array.from(new Set([...h, ...msg.hidden.map(String)])));
                }
                break;
            case 'front_query':
                if (isFrontTab()) channel.post({ type: 'front_reply', practiceId, callId: msg.callId });
                break;
            case 'front_reply':
                frontRepliesRef.current.add(String(msg.callId));
                break;
            default:
            }
        });
        channel.post({ type: 'sync_request', practiceId });
        return () => { unsubscribe(); channel.close(); channelRef.current = null; };
    }, [active, userId, practiceId, ingest]);

    // 3. Live-calls endpoint.
    const refetch = useCallback(async () => {
        if (!active || liveEndpointMissingRef.current) return;
        const requestScope = scope;
        const requestedAt = Date.now();
        let response;
        try {
            // Background read: a 500/503 must not pop "Something went wrong".
            response = await callApi(liveCallsUrl(practiceId), 'GET', null, false, { suppressServerErrorDispatch: true });
        } catch { return; }
        // The user or practice changed while this was in flight: not ours.
        if (scopeRef.current !== requestScope) return;
        // An older backend (no endpoint) or no Call Logs access: stop asking for
        // this session.
        if (response?.status === 403 || response?.status === 404 || response?.status === 405) { liveEndpointMissingRef.current = true; return; }
        // callApi never throws; status 0 is no response. Keep what we have.
        if (!response?.ok) return;
        const rows = rowsOf(response.data);
        if (!rows) return;
        setCalls((prev) => reconcileLiveCalls(prev, rows, { requestedAt }));
    }, [active, scope, practiceId]);

    useEffect(() => { refetch(); }, [refetch]);

    useEffect(() => {
        if (!active) return undefined;
        let hiddenAt = document.hidden ? Date.now() : null;
        const onVisibility = () => {
            if (document.hidden) { hiddenAt = Date.now(); return; }
            if (hiddenAt && Date.now() - hiddenAt > REFETCH_AFTER_HIDDEN_MS) refetch();
            hiddenAt = null;
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('online', refetch);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('online', refetch);
        };
    }, [active, refetch]);

    // While a call is in progress, re-read the endpoint so a lost end event
    // (socket reconnect, dropped message) closes the pill within a minute.
    const live = hasLiveCall(calls);
    useEffect(() => {
        if (!active || !live) return undefined;
        const timer = setInterval(() => { if (!document.hidden) refetch(); }, LIVE_RECHECK_MS);
        return () => clearInterval(timer);
    }, [active, live, refetch]);

    // Expiry: one timer for the next call due to close.
    useEffect(() => {
        const wait = nextExpiryIn(calls);
        if (wait == null) return undefined;
        const timer = setTimeout(() => setCalls((prev) => expireCalls(prev, Date.now())), wait);
        return () => clearTimeout(timer);
    }, [calls]);

    // Hidden ids of calls that are gone are forgotten.
    useEffect(() => {
        setHidden((h) => {
            const kept = h.filter((id) => calls[id] && !calls[id].closed);
            return kept.length === h.length ? h : kept;
        });
        // The call the user picked has finished: back to the usual order.
        setFocusedId((id) => (id && calls[id] && !calls[id].closed ? id : null));
        // Nothing left on screen: the next call comes back to the default spot.
        if (Object.values(calls).every((c) => c.closed)) setPositionState(DEFAULT_POSITION);
    }, [calls]);

    // Desktop notification: once per inbound ringing call (outbound ringing is
    // your own dial), only when no CRM tab is in front, closed once it stops
    // ringing.
    useEffect(() => {
        const open = notificationsRef.current;
        Object.values(calls).forEach((c) => {
            if (c.closed || c.state !== CALL_STATE.RINGING || c.direction !== 'inbound' || open.has(c.callId)) return;
            if (dndRef.current || hidden.includes(c.callId)) return;
            open.set(c.callId, null);
            // The short ring, when this user turned call sound on: once per call, from one tab,
            // whether or not the CRM is in front (CU-869fmcc9r). The browser only plays it after
            // a click on the page since it loaded (unlockRingTone).
            if (soundOn(userId) && canPlayRing()) claimRing(c.callId).then((mine) => { if (mine) playRingTone(); });
            if (isFrontTab()) return;
            frontRepliesRef.current.delete(c.callId);
            channelRef.current?.post({ type: 'front_query', practiceId, callId: c.callId });
            const timer = setTimeout(() => {
                pendingTimersRef.current.delete(timer);
                const now = callsRef.current[c.callId];
                if (frontRepliesRef.current.has(c.callId) || !now || now.closed || now.state !== CALL_STATE.RINGING) return;
                // No CRM tab in front: the desktop notification (CU-869fmcc9r).
                open.set(c.callId, showDesktopNotification(notificationFor(now, { deskName: deskNameRef.current }), {
                    onClick: () => onClickRef.current?.(callsRef.current[c.callId] || now),
                }));
            }, FRONT_TAB_WAIT_MS);
            pendingTimersRef.current.add(timer);
        });
        open.forEach((n, callId) => {
            const c = calls[callId];
            if (c && !c.closed && c.state === CALL_STATE.RINGING) return;
            try { n?.close(); } catch { /* already closed */ }
            if (n) open.set(callId, null);
            // Forget the id only once even the tombstone is gone, so a call
            // never notifies twice.
            if (!c) { open.delete(callId); forgetRing(callId); }
        });
    }, [calls, hidden, practiceId, userId]);

    // A tab that closes must not leave its notifications behind.
    useEffect(() => {
        const closeAll = () => notificationsRef.current.forEach((n) => { try { n?.close(); } catch { /* ignore */ } });
        window.addEventListener('pagehide', closeAll);
        return () => {
            window.removeEventListener('pagehide', closeAll);
            pendingTimersRef.current.forEach(clearTimeout);
            closeAll();
        };
    }, []);

    // Do not disturb: same tab (DND_EVENT from the user menu) and other tabs
    // (`storage`).
    useEffect(() => {
        const onDnd = (e) => { if (String(e?.detail?.userId) === String(userId)) setDndState(Boolean(e.detail.on)); };
        const onStorage = (e) => {
            if (e.key === dndKey(userId)) setDndState(e.newValue === '1');
            if (e.key === minimisedKey(userId)) setMinimisedState(e.newValue === '1');
        };
        window.addEventListener(DND_EVENT, onDnd);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(DND_EVENT, onDnd);
            window.removeEventListener('storage', onStorage);
        };
    }, [userId]);

    const hide = useCallback((callId) => {
        const id = String(callId);
        setHidden((h) => (h.includes(id) ? h : [...h, id]));
        channelRef.current?.post({ type: 'hide', practiceId, callId: id });
    }, [practiceId]);

    const unhideAll = useCallback(() => {
        setHidden([]);
        channelRef.current?.post({ type: 'unhide_all', practiceId });
    }, [practiceId]);

    const dismiss = useCallback((callId) => {
        const id = String(callId);
        // Closing an "answered elsewhere" pill is not final: a later transfer to this user
        // (a newer event) still opens a full pill. Everything else is final.
        const final = callsRef.current?.[id]?.state !== CALL_STATE.ANSWERED_ELSEWHERE;
        setCalls((prev) => tombstone(prev, id, { final, occurredAt: final ? null : prev?.[id]?.occurredAt ?? null }));
        channelRef.current?.post({ type: 'dismiss', practiceId, callId: id, final });
    }, [practiceId]);

    // "+N": show the next visible call on the pill. The ordered list already starts at
    // the call on screen, so its second entry is the next one; one more press comes back
    // round to the first.
    const focusNext = useCallback(() => {
        setFocusedId((current) => {
            const order = selectView(callsRef.current, {
                hidden: hiddenRef.current, dnd: dndRef.current, focusedId: current,
            }).visible;
            return order.length > 1 ? order[1].callId : current;
        });
    }, []);

    const setMinimised = useCallback((on) => {
        setMinimisedState(Boolean(on));
        writeMinimised(userId, on);
    }, [userId]);

    const setPosition = useCallback((pos) => {
        setPositionState(pos);
    }, []);

    const view = useMemo(() => selectView(calls, { hidden, dnd, focusedId }), [calls, hidden, dnd, focusedId]);

    return { ...view, calls, dnd, position, setPosition, minimised, setMinimised, hide, unhideAll, dismiss, refetch, focusNext };
}
